// TODO: Make sure that resize trigger away from mobileMenu resets open state to closed.

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  Renderer2,
  signal,
  viewChild,
  viewChildren
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';

import type { LogoModel } from '@amfam/shared/models/lib/brand/logo.model';
import { RouterLinkWithHrefDirective } from '@amfam/shared/ui/ds-router-link/lib/router-link.directive';
import { BrandLinkDataModel } from '@amfam/shared/utility/brand/lib/models/brand.model';
import { WindowRef } from '@amfam/shared/utility/shared-services/lib/window/window.service';

import {
  LinkClickEvent,
  MobileLinkClickEvent,
  MobileSublinkClickEvent,
  NavLinkTreeObj,
  SublinkClickEvent
} from '../../models/links-model';
import { NotificationObj } from '../../models/notification-model';
import { HeaderWrapperService } from '../../services/header-wrapper.service';
import { DsHeaderMobileNavigationItemComponent } from './ds-header-mobile-navigation-item/ds-header-mobile-navigation-item.component';
import { DsHeaderMobileNotificationComponent } from './ds-header-mobile-notification/ds-header-mobile-notification.component';

@Component({
  selector: 'ds-header-mobile-navigation',
  templateUrl: './ds-header-mobile-navigation.component.html',
  styleUrls: [
    './ds-header-mobile-navigation.component.scss',
    './shared/ds-header-mobile-navigation.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DsHeaderMobileNotificationComponent,
    DsHeaderMobileNavigationItemComponent,
    RouterLinkWithHrefDirective
  ]
})
export class DsHeaderMobileNavigationComponent implements OnInit {
  private headerWrapperService = inject(HeaderWrapperService);
  private win = inject(WindowRef);
  private renderer = inject(Renderer2);
  private destroyRef = inject(DestroyRef);

  // An event stream, not template state; see DsHeaderComponent.
  readonly loaded$ = input<Observable<boolean>>();
  readonly headerLogo = input<LogoModel | null>();
  readonly brandInfo = input<BrandLinkDataModel | null>();
  readonly loggedIn = input<boolean | null>();
  readonly homeUrl = input<string>();
  readonly linkArray = input<NavLinkTreeObj[] | null>();
  readonly notifications = input<NotificationObj[] | null>();

  readonly linkClickEvent = output<LinkClickEvent>();
  readonly sublinkClickEvent = output<SublinkClickEvent>();

  readonly navItems = viewChildren<DsHeaderMobileNavigationItemComponent>('navItem');
  readonly menuLink = viewChild<ElementRef>('menuLink');

  readonly mobileMenuOpen = signal(false);
  private readonly showNotificationMenuSub = toSignal(
    this.headerWrapperService.mobileNotificationMenuOpen$,
    { initialValue: false }
  );
  readonly mobileLinkArray = computed(() =>
    [...(this.linkArray() ?? [])].sort(
      (a, b) => (a.mobileMenuItemOrder ?? 0) - (b.mobileMenuItemOrder ?? 0)
    )
  );

  ngOnInit() {
    // Needs to listen on the entire body opposed to @HostListener
    this.renderer.listen(document.body, 'keyup', event => {
      this.keyupEventHandler(event);
    });

    this.headerWrapperService.mobileMenuOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mobileMenuOpen => {
        this.mobileMenuOpen.set(mobileMenuOpen);
        if (mobileMenuOpen) {
          this.win.nativeWindow.scrollTo(0, 0);
        }
      });
    this.headerWrapperService.mobileMenuReset$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mobileMenuReset => {
        if (mobileMenuReset) {
          this.menuLink()?.nativeElement.focus();
          this.headerWrapperService.setMobileMenuReset(false);
        }
      });
  }

  resetOnBlur(target: DsHeaderMobileNavigationItemComponent | undefined) {
    if (target === this.navItems().at(-1)) {
      this.headerWrapperService.setMobileMenuReset(true);
    }
  }

  hideAllNavItems() {
    this.navItems().forEach(mobileNavItemInstance => {
      mobileNavItemInstance.showNavItem.set('hide');
    });
  }

  showAllNavItems() {
    this.navItems().forEach(mobileNavItemInstance => {
      mobileNavItemInstance.showNavItem.set('show');
    });
  }

  linkClicked({ link, event }: MobileLinkClickEvent) {
    // link.link is a signal, need to call it as a function to get the value
    const linkItem = link?.link?.();
    if (linkItem) {
      this.linkClickEvent.emit({ link: linkItem, event });
    }

    if (linkItem?.subLinkArray?.length) {
      this.hideAllNavItems();
    } else if (!linkItem?.textOnly) {
      this.headerWrapperService.setMobileMenuOpen(false);
    }
  }

  sublinkClicked({ sublink, event }: MobileSublinkClickEvent) {
    if (sublink) {
      this.sublinkClickEvent.emit({ sublink, event });
    }

    // Reset to navItems unless specified otherwise with the textOnly property
    if (!sublink?.textOnly) {
      this.showAllNavItems();
    }
  }

  sublinkTitleClicked() {
    this.showAllNavItems();
  }

  // ESC has to close menu for WCAG 2.0
  keyupEventHandler(event: KeyboardEvent) {
    if (this.mobileMenuOpen()) {
      if (event.key === 'Escape') {
        this.headerWrapperService.setMobileMenuOpen(false);
      }
    }
  }

  toggleNav() {
    this.headerWrapperService.setMobileMenuOpen(!this.mobileMenuOpen());
    if (this.showNotificationMenuSub()) {
      this.headerWrapperService.setMobileNotificationMenuOpen(false);
    }
  }
}
